import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { Cpu, X, Zap } from "lucide-react";
import { API } from "./shopData";

const POPULAR_CODES = ["2JZ", "1JZ", "RB26", "RB25", "SR20", "4G63", "K20", "K24", "B16", "B18", "EJ20", "EJ25", "M50", "M52", "S54", "B58", "N54", "VR6", "13B", "4B11"];

export default function EngineCodeFilter({ brand, productCategory, carMake, onFilter, activeEngineCode }) {
  const [engineCodes, setEngineCodes] = useState([]);
  const [selected, setSelected] = useState(activeEngineCode || "");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const contextLabel = brand || productCategory || carMake || "";

  useEffect(() => {
    if (!brand && !productCategory && !carMake) { setEngineCodes([]); return; }
    setLoading(true);
    const params = {};
    if (brand) params.brand = brand;
    if (productCategory) params.product_category = productCategory;
    if (carMake) params.car_make = carMake;
    axios.get(`${API}/products/engine-codes`, { params })
      .then((r) => setEngineCodes(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [brand, productCategory, carMake]);

  useEffect(() => {
    setSelected(activeEngineCode || "");
    if (!activeEngineCode) setQuery("");
  }, [activeEngineCode, brand, productCategory, carMake]);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return engineCodes.slice(0, 100);
    return engineCodes.filter((ec) => ec.engine.toLowerCase().includes(q)).slice(0, 100);
  }, [engineCodes, query]);

  const popularChips = useMemo(() =>
    POPULAR_CODES.filter((p) => engineCodes.some((ec) => ec.engine.toLowerCase().includes(p.toLowerCase()))).slice(0, 10),
  [engineCodes]);

  const applyCode = (code) => {
    setSelected(code);
    setQuery(code);
    setOpen(false);
    onFilter(code || null);
  };

  const clearFilter = () => {
    setSelected("");
    setQuery("");
    setOpen(false);
    onFilter(null);
  };

  if (engineCodes.length === 0 && !loading) return null;

  return (
    <div data-testid="engine-code-filter" className="bg-[#111] border border-[#1E1E1E] rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Cpu size={15} className="text-[#FF3B30]" />
          <span className="text-white text-xs font-bold uppercase tracking-wider">Zoek op motorcode</span>
        </div>
        {selected && (
          <button onClick={clearFilter} className="flex items-center gap-1 text-[#666] hover:text-white text-xs transition-colors" data-testid="engine-filter-clear">
            <X size={12} /> Wissen
          </button>
        )}
      </div>

      {popularChips.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="popular-engine-codes">
          <Zap size={12} className="text-[#FF3B30] flex-shrink-0" />
          {popularChips.map((code) => (
            <button key={code} data-testid={`popular-code-${code.toLowerCase()}`}
              onClick={() => (selected === code ? clearFilter() : applyCode(code))}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all border ${selected === code ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#0A0A0A] text-[#888] border-[#1E1E1E] hover:text-white hover:border-[#FF3B30]/30"}`}>
              {code}
            </button>
          ))}
        </div>
      )}

      <div className="relative" ref={wrapRef}>
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); if (!e.target.value && selected) clearFilter(); }}
          onFocus={() => setOpen(true)}
          placeholder={loading ? "Laden..." : `Typ een motorcode... bijv. 2JZ, K20A, 4G63 (${engineCodes.length} codes)`}
          disabled={loading}
          data-testid="engine-code-search-input"
          className="w-full bg-[#0A0A0A] border border-[#1E1E1E] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-[#444] focus:border-[#FF3B30]/50 focus:outline-none transition-colors"
        />
        {open && filtered.length > 0 && (
          <div data-testid="engine-code-dropdown" className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-[#0D0D0D] border border-[#1E1E1E] rounded-lg shadow-xl">
            {filtered.map((ec) => (
              <button key={ec.engine} onClick={() => applyCode(ec.engine)}
                className={`w-full text-left px-3 py-2 text-xs transition-colors flex justify-between gap-2 ${selected === ec.engine ? "bg-[#FF3B30]/10 text-[#FF3B30]" : "text-[#AAA] hover:bg-[#161616] hover:text-white"}`}>
                <span>{ec.engine}</span>
                <span className="text-[#555] flex-shrink-0">{ec.count}</span>
              </button>
            ))}
          </div>
        )}
        {open && !loading && filtered.length === 0 && query && (
          <div className="absolute z-30 left-0 right-0 mt-1 bg-[#0D0D0D] border border-[#1E1E1E] rounded-lg px-3 py-2.5 text-xs text-[#555]">
            Geen motorcodes gevonden voor &ldquo;{query}&rdquo;
          </div>
        )}
      </div>

      {selected && (
        <p className="text-[#555] text-[10px] mt-2">
          Toont {contextLabel} producten voor motorcode {selected}
        </p>
      )}
    </div>
  );
}
